/* Aria Robotics Admin — shared UI primitives (theme-token styled). */
import * as React from 'react';
import { ClientResponseError } from 'pocketbase';
import { Icon } from '@/ui';

type CSS = React.CSSProperties;

// ---- PocketBase error → readable German message ----
interface PbFieldError { code?: string; message?: string; }
interface PbBody { message?: string; data?: Record<string, PbFieldError>; }
export function pbError(e: unknown): string {
  if (e instanceof ClientResponseError) {
    const body = e.data as PbBody | undefined;
    const fields = body?.data;
    if (fields && Object.keys(fields).length) {
      return Object.entries(fields).map(([k, v]) => `${k}: ${v.message ?? 'ungültig'}`).join(' · ');
    }
    return body?.message ?? e.message ?? 'Unbekannter Fehler';
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler';
}

// ---- Field label wrapper ----
const labelStyle: CSS = { fontSize: 13.5, fontWeight: 500, color: 'var(--text-100)', display: 'block', marginBottom: 6 };
const inputStyle: CSS = {
  width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-sans)', fontSize: 15,
  color: 'var(--text-100)', background: 'var(--surface-page)', padding: '10px 12px',
  borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', outline: 'none',
};

export interface FieldWrapProps { label?: string; required?: boolean; hint?: React.ReactNode; error?: string; children: React.ReactNode; }
export function FieldWrap({ label, required, hint, error, children }: FieldWrapProps) {
  return (
    <div style={{ marginBottom: 2 }}>
      {label && <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--accent-200)' }}> *</span>}</label>}
      {children}
      {hint && <div style={{ fontSize: 12.5, color: 'var(--text-200)', marginTop: 5 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12.5, color: '#e5484d', marginTop: 5 }}>{error}</div>}
    </div>
  );
}

export interface TextFieldProps {
  label?: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean; hint?: React.ReactNode; error?: string;
  autoFocus?: boolean; name?: string; maxLength?: number; disabled?: boolean;
}
export function TextField({ label, value, onChange, type = 'text', placeholder, required, hint, error, autoFocus, name, maxLength, disabled }: TextFieldProps) {
  return (
    <FieldWrap label={label} required={required} hint={hint} error={error}>
      <input
        type={type} value={value} name={name} placeholder={placeholder} required={required}
        autoFocus={autoFocus} maxLength={maxLength} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
      />
    </FieldWrap>
  );
}

export interface TextAreaFieldProps {
  label?: string; value: string; onChange: (v: string) => void;
  rows?: number; placeholder?: string; required?: boolean; hint?: React.ReactNode; maxLength?: number;
}
export function TextAreaField({ label, value, onChange, rows = 4, placeholder, required, hint, maxLength }: TextAreaFieldProps) {
  return (
    <FieldWrap label={label} required={required} hint={hint}>
      <textarea
        value={value} rows={rows} placeholder={placeholder} required={required} maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </FieldWrap>
  );
}

export interface SelectOption { value: string; label: string; }
export interface SelectFieldProps {
  label?: string; value: string; onChange: (v: string) => void; options: SelectOption[]; required?: boolean; hint?: React.ReactNode;
}
export function SelectField({ label, value, onChange, options, required, hint }: SelectFieldProps) {
  return (
    <FieldWrap label={label} required={required} hint={hint}>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldWrap>
  );
}

export interface CheckboxProps { label: React.ReactNode; checked: boolean; onChange: (v: boolean) => void; }
export function Checkbox({ label, checked, onChange }: CheckboxProps) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, color: 'var(--text-100)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: 'var(--primary-200)', width: 16, height: 16 }} />
      {label}
    </label>
  );
}

export function Card({ children, style }: { children: React.ReactNode; style?: CSS }) {
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: 24, boxShadow: 'var(--shadow-sm)', ...style }}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-100)', margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14.5, color: 'var(--text-200)', margin: '6px 0 0' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10 }}>{actions}</div>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-200)', fontSize: 14, padding: 24 }}>
      <span className="ar-spin" style={{ display: 'inline-flex' }}><Icon name="loader-circle" size={18} /></span>
      {label ?? 'Lädt…'}
    </div>
  );
}

// ---- Modal ----
export function Modal({ title, onClose, children, footer, width = 480 }: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number }) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="ar-admin" style={{ width: '100%', maxWidth: width, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-100)', margin: 0 }}>{title}</h2>
          <button onClick={onClose} aria-label="Schließen" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-200)', display: 'inline-flex' }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', borderTop: '1px solid var(--border-subtle)' }}>{footer}</div>}
      </div>
    </div>
  );
}

// ---- Toasts ----
type ToastTone = 'success' | 'error' | 'info';
interface ToastItem { id: number; msg: string; tone: ToastTone; }
interface ToastCtx { show: (msg: string, tone?: ToastTone) => void; }
const ToastContext = React.createContext<ToastCtx | null>(null);
let toastSeq = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const show = React.useCallback((msg: string, tone: ToastTone = 'info') => {
    const id = ++toastSeq;
    setItems((prev) => [...prev, { id, msg, tone }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 4200);
  }, []);
  const colors: Record<ToastTone, string> = { success: '#30a46c', error: '#e5484d', info: 'var(--primary-200)' };
  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 100, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
        {items.map((t) => (
          <div key={t.id} style={{ background: 'var(--surface-card)', border: `1px solid var(--border-default)`, borderLeft: `3px solid ${colors[t.tone]}`, borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: '12px 16px', fontSize: 14, color: 'var(--text-100)' }}>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export function useToast(): ToastCtx {
  const c = React.useContext(ToastContext);
  if (!c) throw new Error('useToast must be used within <ToastProvider>');
  return c;
}
